import {IconButton, Chip} from '@material-ui/core';
import {EmptyDateRange} from 'Core/models/DateRange';
import {FilterOptionValue} from 'Core/models/FilterOption';
import * as O from 'fp-ts/lib/Option';
import React from 'react';
import {FilterProperties} from 'Shared/components/PagedView/Filter';
import {AdditionalFilterProperty, Column} from 'Shared/components/PagedView/PagedView';
import AdditionalFiltersButton from 'Shared/components/PagedView/Table/AdditionalFiltersButton';
import ColumnHeader from 'Shared/components/PagedView/Table/ColumnHeader';
import FilterChip from 'Shared/components/PagedView/Table/FilterChip';
import 'Shared/components/PagedView/Table/FilterHeader.sass';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import notEmpty from 'Shared/utilities/notEmpty';
import {prop} from 'Shared/utilities/prop';
import {useDispatch, useSelector} from 'react-redux';
import {changeMarketingFilters} from 'Agent/AgentForms/Marketing/ducks/Marketing';
import {ReducerState} from 'rootReducer';
import {changeAnalyticsState} from 'Core/ducks/analytics';

type TableHeaderProps<TData> = {
  onOpenColumnDrawer: () => void;
  onColumnResize: (columnKey: string, newSize: number) => void;
  isScrollbarShowing: boolean;
  eventAction?: string;
  sort: Sort | undefined;
  onChangeSort: ((sort?: Sort) => void) | undefined;
  actions?: JSX.Element;
  additionalFilterProperties: AdditionalFilterProperty[] | undefined;
  columnsForHeaders: Array<Column<TData>>;
  allColumns: Array<Column<TData>>;
  titleOrType?: string | undefined;
};

const NoFiltersAppliedMessage = <i>No Filters Applied</i>;
export const getIsFilterAppliedForColumn = (filter: FilterProperties<any>) => {
  const filterData = filter.filterData[filter.filterKey];
  const isEmptyInequalityRange = O.isNone;

  return (
    filterData &&
    filterData !== EmptyDateRange &&
    (filterData.startDate !== '' || filterData.endDate !== '') &&
    !FilterOptionValue.is.EmptyOption(filterData) &&
    (!(filterData instanceof Array) || filterData.some(fd => !FilterOptionValue.is.EmptyOption(fd))) &&
    !isEmptyInequalityRange(filterData)
  );
};

const TableHeader = <TData extends {}>(props: TableHeaderProps<TData>) => {
  const {
    columnsForHeaders,
    allColumns,
    actions,
    additionalFilterProperties,
    isScrollbarShowing,
    onColumnResize,
    onOpenColumnDrawer,
    onChangeSort,
    sort,
    titleOrType,
  } = props;
  const {filters} = useSelector((state: ReducerState) => state.marketing);
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const handlePagedTableFilterColumnsBtn = () => {
    onOpenColumnDrawer();
    dispatch(changeAnalyticsState({page: analytics.page, ctaResponse: {eventAction: props.eventAction}}));
  };

  const dispatch = useDispatch();
  const addfilters = [...allColumns.map(({filter, title}) => ({filter, title})), ...(additionalFilterProperties || [])];
  const hasFilterableColumns = addfilters.map(prop('filter')).filter(notEmpty).length > 0;
  const hasAnyFiltersCurrentlyApplied = !addfilters
    .map(prop('filter'))
    .filter(notEmpty)
    .some(getIsFilterAppliedForColumn);

  return (
    <>
      <div className="filter-header" data-dd-privacy="mask" role="presentation">
        {hasFilterableColumns && (
          <div className="filter-header__filters-list">
            <span data-dd-privacy="mask" id="filter-header-label" className="filter-header__filters-label">
              Filters:
            </span>
            {titleOrType !== '' && titleOrType !== undefined && (
              <Chip
                id={`filter-header-chip-titleOrType`}
                variant="outlined"
                className="filter-header__chip"
                color="primary"
                onDelete={() => dispatch(changeMarketingFilters({...filters, titleOrType: ''}))}
                label={
                  <>
                    <strong>{'Keyword'}:&nbsp;</strong>
                    {titleOrType as string}
                  </>
                }
              />
            )}
            {hasAnyFiltersCurrentlyApplied && (titleOrType === '' || titleOrType === undefined) ? (
              <span id="filter-header-default">{NoFiltersAppliedMessage}</span>
            ) : (
              addfilters.map(
                ({filter, title}) =>
                  filter &&
                  getIsFilterAppliedForColumn(filter) && (
                    <FilterChip key={`filter-chip__${title}`} title={title} filter={filter} />
                  )
              )
            )}
          </div>
        )}
        {additionalFilterProperties && (
          <AdditionalFiltersButton additionalFilterProperties={additionalFilterProperties} />
        )}
        <div className="filter-header__actions">{actions}</div>
      </div>
      <div role="rowgroup">
        <div
          className={isScrollbarShowing ? 'paged-table__header-row' : 'paged-table__header-row--scrollable'}
          role="row"
        >
          {columnsForHeaders.map(({title, sortColumnKey, filter}, index) => (
            <ColumnHeader
              key={`column-cell-header__${title}__${index}`}
              isLastColumn={columnsForHeaders.length === index + 1}
              sort={sort}
              onColumnResize={onColumnResize}
              onChangeSort={onChangeSort}
              sortColumnKey={sortColumnKey}
              filterProperties={filter}
              title={title}
              titleOrType={titleOrType}
              idx={index}
            />
          ))}
          <IconButton
            id={`paged-table-filter-columns-btn`}
            className="paged-table__filter-columns-filter-button"
            size="small"
            color="primary"
            onClick={handlePagedTableFilterColumnsBtn}
            aria-label="Column Preferences"
            aria-hidden="false"
          >
            <i className="fa fa-sliders-h" />
          </IconButton>
        </div>
      </div>
    </>
  );
};

export default TableHeader;
